import type { ApiResponse } from '@bitstopco/bitstop-theme';

import { normalizeTransactionRedemption } from '@/helpers/redemption';
import api from '@/store/api';
import { athena, unity } from '@/store/api/instances';

import type {
  AthenaTransactionRedemptionView,
  CancelRedemptionSettlementRequest,
  CreateRedemptionSettlementRequest,
  RedemptionMethod,
  RedemptionSettlement,
  TransactionRedemption,
} from '@/types';

type CreateRedemptionSettlementResult = {
  settlement?: RedemptionSettlement;
  notesSaved: boolean;
};

export const redemptionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionRedemption: build.query<ApiResponse<TransactionRedemption>, string>({
      query: (id) => athena({ url: `v2/transaction/${id}/redemption` }),
      transformResponse: (response: ApiResponse<AthenaTransactionRedemptionView>) => ({
        ...response,
        data: normalizeTransactionRedemption(response?.data),
      }),
      providesTags: (_, __, id) => [{ type: 'Redemption', id }],
    }),
    getRedemptionMethods: build.query<ApiResponse<RedemptionMethod[]>, string>({
      query: (obligationId) => unity({ url: `redemptions/${obligationId}/methods` }),
      providesTags: (_, __, obligationId) => [{ type: 'RedemptionMethods', id: obligationId }],
    }),
    // Unity's create-settlement payload has no notes field (pydantic drops it silently), so
    // notes are saved with a follow-up PUT. A failed notes save doesn't fail the issue -- the
    // settlement already exists and a retry would 400 ("Redemption settlements already
    // created") -- it's reported via `notesSaved` instead.
    createRedemptionSettlement: build.mutation<
      CreateRedemptionSettlementResult,
      { obligationId: string; txId: string; body: CreateRedemptionSettlementRequest }
    >({
      queryFn: async ({ obligationId, body }, _queryApi, _extraOptions, fetchWithBQ) => {
        const { notes, ...createBody } = body;
        const createResponse = await fetchWithBQ(
          unity({
            url: `redemptions/${obligationId}/settlements`,
            method: 'POST',
            data: createBody,
          }),
        );
        if (createResponse.error) return { error: createResponse.error };

        const settlement = (createResponse.data as ApiResponse<RedemptionSettlement>)?.data;
        const settlementId = settlement?.id;
        if (!notes || !settlementId) return { data: { settlement, notesSaved: true } };

        const notesResponse = await fetchWithBQ(
          unity({
            url: `redemptions/settlements/${settlementId}`,
            method: 'PUT',
            data: { notes },
          }),
        );
        return { data: { settlement, notesSaved: !notesResponse.error } };
      },
      // Issuing or cancelling changes which methods Unity allows (obligation_locked), so the
      // methods list refetches too instead of showing stale locks until a page refresh.
      invalidatesTags: (_, __, { txId }) => [
        { type: 'Redemption', id: txId },
        { type: 'Transaction', id: txId },
        'RedemptionMethods',
      ],
    }),
    cancelRedemptionSettlement: build.mutation<
      ApiResponse<RedemptionSettlement>,
      { settlementId: string; txId: string; body: CancelRedemptionSettlementRequest }
    >({
      query: ({ settlementId, body }) =>
        unity({
          url: `redemptions/settlements/${settlementId}/cancel`,
          method: 'POST',
          data: body,
        }),
      // Issuing or cancelling changes which methods Unity allows (obligation_locked), so the
      // methods list refetches too instead of showing stale locks until a page refresh.
      invalidatesTags: (_, __, { txId }) => [
        { type: 'Redemption', id: txId },
        { type: 'Transaction', id: txId },
        'RedemptionMethods',
      ],
    }),
  }),
});

export const {
  useGetTransactionRedemptionQuery,
  useGetRedemptionMethodsQuery,
  useCreateRedemptionSettlementMutation,
  useCancelRedemptionSettlementMutation,
} = redemptionsApi;

export const {
  endpoints: {
    getTransactionRedemption,
    getRedemptionMethods,
    createRedemptionSettlement,
    cancelRedemptionSettlement,
  },
} = redemptionsApi;
